import { useState, useEffect, useMemo, useCallback } from "react";
import TabPanel from "devextreme-react/tab-panel";
import Grid from "./SARGrid";
import { useParams } from "react-router";
import { useLTIP } from "@hooks/useLTIP";
import CustomButton from "@components/CustomButton";
import { useStockPrice } from "@hooks/useStockPrice";
import NumberBoxWrapper from "@/components/NumberBoxWrapper";
import { TextSkeleton } from "@/components/Skeletons";
import { useLocalStorage } from "@hooks/useLocalStorage";
import NoDataMessage from "@/components/NoDataMessage";
import withDisclaimer from "@/hoc/withDisclaimer";
import HouseholdClientTabs from "@/components/HouseholdClientTabs";
import StockPriceTooltip from "@/components/StockPriceTooltip";
import { FinancialAccount } from "@/types/types";
import { SarSkeleton } from "./SarSkeleton";
import { LTIPType } from "@/types/enums";
import useSelectedClient from "@/hooks/useSelectedClient";
import { checkClientsBirthdates, getOwnerName } from "@/helper/helper";
import useDefaultSelectedClient from "@/hooks/useDefaultSelectedClient";
import useHeaderChange from "@/hooks/useHeaderChange";
import { NoContactBirthDateMessage } from "../HouseholdContact";

function StockAppreciationRights() {
  const params = useParams();
  const { readData, saveData, removeItem, getDataWithTTL, saveDataWithTTL } = useLocalStorage();
  const { household } = useHeaderChange(params.id as string, "Stock Appreciation Rights");
  const [stockPrice, setStockPrice] = useState(0);
  const [tabIndex, setTabIndex] = useState<number | null>(null);
  const { selectedClient, setSelectedClient } = useSelectedClient(household, LTIPType.SARs);

  const {
    data,
    isLoading: sarsLoading,
    isError: sarsError,
    isSuccess: sarsFetched,
    isRefetching,
  } = useLTIP(household?.externalCode as string, LTIPType.SARs, selectedClient, household);

  useDefaultSelectedClient(household, data, selectedClient, setSelectedClient, LTIPType.SARs);

  const {
    data: stockInfo,
    isSuccess: stockInfoFetched,
    isLoading: stockLoading,
  } = useStockPrice(data?.data[tabIndex ?? 0]?.ticker);

  useEffect(() => {
    const localStockPrice = getDataWithTTL(`sars-${selectedClient}-${tabIndex}`);

    if (localStockPrice && Number(localStockPrice)) {
      setStockPrice(Number(localStockPrice));
    } else setStockPrice(stockInfo?.data?.value ?? 0);
  }, [tabIndex, stockInfoFetched, selectedClient]);

  const sarsForClient = useMemo(() => {
    if (sarsFetched && selectedClient) {
      return data?.data.map((item, i) => {
        return { ...item, index: i, price: stockPrice };
      });
    } else return [];
  }, [sarsFetched, tabIndex, stockPrice]);

  useEffect(() => {
    if (selectedClient) {
      const selectedTabLocal = readData(
        `sars-selected-client-tab-${selectedClient}-${household?.externalCode}-${household?.clientCode}`
      );
      if (selectedTabLocal !== null && selectedTabLocal != "undefined") {
        setTabIndex(parseInt(selectedTabLocal) ?? 0);
      } else {
        setTabIndex(0);
      }
    }
  }, [selectedClient]);

  useEffect(() => {
    if (tabIndex !== null && tabIndex !== undefined) {
      saveData(
        `sars-selected-client-tab-${selectedClient}-${household?.externalCode}-${household?.clientCode}`,
        tabIndex.toString()
      );
    }
  }, [selectedClient, tabIndex]);

  const onTabSelectionChanged = useCallback(
    (e) => {
      const selectedItem = e.addedItems[0] as FinancialAccount;
      const selectedIndex = sarsForClient?.findIndex(
        (tab) => tab.externalCode === selectedItem.externalCode
      );
      if (tabIndex === selectedIndex) return;
      setTabIndex(selectedIndex ?? 0);
    },
    [sarsForClient, selectedClient, tabIndex]
  );

  const itemTitleRender = useCallback(
    (fa: FinancialAccount) => {
      return (
        <span
          className={`px-5 text-base ${fa === sarsForClient[tabIndex ?? 0] ? "font-medium" : ""}`}
        >
          {fa?.ticker ?? "—"}
        </span>
      );
    },
    [sarsForClient, tabIndex]
  );

  if (sarsLoading || isRefetching) {
    return <SarSkeleton />;
  }

  if (sarsError) {
    return <div>Failed fetching Stock Appreciation Rights</div>;
  }

  if (checkClientsBirthdates(household)) {
    return <NoContactBirthDateMessage household={household} />;
  }

  return (
    <div className="print-zoom-75 relative text-neutral-100">
      <div id="stock-combined-values" key={selectedClient} className="mb-2 w-1/2" />
      <h2 className={`absolute top-[202px] text-lg font-medium`}>
        {tabIndex !== null ? sarsForClient[tabIndex]?.name : ""}{" "}
        {household?.secondaryContact && !data?.bothClientsHaveData
          ? `for ${getOwnerName(household, selectedClient)}`
          : ""}{" "}
      </h2>
      <div className="py-[18px]" />
      {household?.secondaryContact && data?.bothClientsHaveData ? (
        <HouseholdClientTabs
          className={`absolute pr-10 ${!sarsForClient?.length ? "top-1" : "top-[250px]"}`}
          household={household}
          selectedClientExternalCode={selectedClient}
          setSelectedClientExternalCode={setSelectedClient}
        />
      ) : null}
      <div
        className={`z-50 ml-auto flex w-fit items-center gap-6 bg-white pl-6 text-neutral-100 ${
          sarsForClient?.length || data?.data.length ? "relative top-[52px]" : ""
        }`}
      >
        {stockPrice !== stockInfo?.data?.value && stockInfoFetched ? (
          <CustomButton
            className="mt-1"
            text="Reset"
            variant="primary"
            onClickHandler={() => {
              setStockPrice(stockInfo?.data?.value ?? 0);
              removeItem(`sars-${selectedClient}-${tabIndex}`);
            }}
          />
        ) : null}
        {stockInfoFetched && (stockInfo.hasError || stockInfo?.hasWarning) && tabIndex ? (
          <StockPriceTooltip
            timestamp={stockInfo?.data?.timestamp}
            ticker={sarsForClient[tabIndex]?.ticker}
          />
        ) : null}
        {tabIndex === null || !sarsForClient[tabIndex]?.ticker ? (
          <div className="h-[48px] w-[148px] text-center">No Ticker</div>
        ) : !sarsForClient?.length || !data?.data.length ? null : stockLoading ? (
          <TextSkeleton height={3} width={9} />
        ) : (
          <NumberBoxWrapper
            format={"$ #,##0.00"}
            stylingMode="outlined"
            labelMode="floating"
            width={148}
            label={`${sarsForClient[tabIndex ?? 0]?.ticker} Stock Price`}
            value={stockPrice ?? 0}
            height={48}
            onValueChange={(val) => {
              setStockPrice(val);

              saveDataWithTTL(`sars-${selectedClient}-${tabIndex}`, val.toString());
            }}
            className="relative bottom-[2px] w-32"
          />
        )}
      </div>
      {!sarsForClient?.length ? (
        <NoDataMessage />
      ) : (
        <TabPanel
          swipeEnabled={false}
          dataSource={sarsForClient}
          itemComponent={Grid}
          id={`sars-tabpanel-${selectedClient}`}
          selectedIndex={tabIndex ?? 0}
          tabIndex={tabIndex ?? 0}
          onContentReady={(e) => {
            if (household?.secondaryContact && data?.bothClientsHaveData) {
              e.element.children[0]?.classList.add("pl-[235px]");
            }
          }}
          onSelectionChanged={onTabSelectionChanged}
          itemTitleRender={itemTitleRender}
          className={`${!sarsForClient?.length ? "pt-[3.25rem]" : ""}`}
          animationEnabled
        />
      )}
    </div>
  );
}

const sarWithDisclaimer = withDisclaimer(StockAppreciationRights);

export default sarWithDisclaimer;
